import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUnbilledSessions } from "@/hooks/useUnbilledSessions";
import { fmtMoney } from "@/lib/invoicing";
import { discardRecap, undoDiscardRecap } from "@/lib/recaps";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Mail,
  Receipt,
  CheckCircle2,
  Zap,
  MoreHorizontal,
  Trash2,
} from "lucide-react";

export const Route = createFileRoute("/app/queue")({
  head: () => ({
    meta: buildAppMeta({
      title: "Recap queue — CoachQuick",
      description: "Review and send automated lesson recap emails before they go out to your clients.",
      path: "/app/queue",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/queue` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <QueuePage />
    </RequireAuth>
  ),
});

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function QueuePage() {
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const draftsKey = ["queue-draft-sessions", userId];

  const draftsQuery = useQuery({
    queryKey: draftsKey,
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("sessions")
        .select("id, session_date, coach_students(student_name)")
        .eq("coach_id", userId!)
        .eq("status", "draft")
        .is("discarded_at" as never, null)
        .order("session_date", { ascending: false });
      return (data ?? []).map((r: any) => ({
        id: r.id as string,
        date: r.session_date as string,
        name: (r.coach_students?.student_name as string) ?? "Client",
      }));
    },
  });

  const nudgesQuery = useQuery({
    queryKey: ["pending-nudges", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("reengagement_nudges" as never)
        .select("id, student_name, days_since_last_session, subject")
        .eq("coach_id", userId!)
        .eq("status", "pending")
        .order("days_since_last_session", { ascending: false });
      return (data ?? []) as unknown as Array<{
        id: string;
        student_name: string;
        days_since_last_session: number;
        subject: string;
      }>;
    },
  });

  const unbilled = useUnbilledSessions();

  const drafts = draftsQuery.data ?? [];
  const nudges = nudgesQuery.data ?? [];
  const billingGroups = unbilled.data?.groups ?? [];

  const total = drafts.length + nudges.length + billingGroups.length;
  const isLoading = draftsQuery.isLoading || nudgesQuery.isLoading || unbilled.isLoading;

  const confirmDraft = drafts.find((d) => d.id === confirmId);

  async function handleDiscard(id: string) {
    setConfirmId(null);

    // Optimistic remove
    const prev = qc.getQueryData<typeof drafts>(draftsKey);
    qc.setQueryData(
      draftsKey,
      (prev ?? []).filter((d) => d.id !== id),
    );

    try {
      await discardRecap(id);
      toast("Recap discarded", {
        description: "The lesson stays in your records.",
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await undoDiscardRecap(id);
              qc.invalidateQueries({ queryKey: ["queue-draft-sessions"] });
              qc.invalidateQueries({ queryKey: ["dashboard-draft-sessions"] });
              qc.invalidateQueries({ queryKey: ["coach-stats"] });
              toast.success("Recap restored");
            } catch {
              toast.error("Couldn't restore the recap");
            }
          },
        },
      });
      qc.invalidateQueries({ queryKey: ["dashboard-draft-sessions"] });
      qc.invalidateQueries({ queryKey: ["coach-stats"] });
    } catch (e) {
      // Rollback
      if (prev) qc.setQueryData(draftsKey, prev);
      toast.error(e instanceof Error ? e.message : "Couldn't discard recap");
    }
  }

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <section className="mb-6 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground">
              YOUR QUEUE
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
              {isLoading
                ? "Loading…"
                : total === 0
                  ? "You're caught up."
                  : `${total} thing${total === 1 ? "" : "s"} to handle`}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Lesson recaps to send, re-engagement drafts to review, and unbilled lessons to
              invoice — all in one place.
            </p>
          </div>
          <Link
            to="/app"
            className="hidden items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground md:inline-flex"
          >
            ← Back to dashboard
          </Link>
        </section>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        ) : total === 0 ? (
          <Card className="flex flex-col items-center gap-3 rounded-2xl p-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <p className="font-display text-xl font-bold">Nothing waiting.</p>
            <p className="text-sm text-muted-foreground">
              No drafts to send, no clients to re-engage, no unbilled lessons. Nice work.
            </p>
            <Link to="/app" className="mt-2">
              <Button variant="outline" size="sm">
                Back to dashboard
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-6">
            <QueueSection
              icon={<Sparkles className="h-4 w-4" />}
              title="Lesson recaps"
              count={drafts.length}
              empty="No recap drafts waiting."
            >
              {drafts.map((d) => (
                <RecapRow
                  key={d.id}
                  id={d.id}
                  name={d.name}
                  date={d.date}
                  onDiscard={() => setConfirmId(d.id)}
                />
              ))}
            </QueueSection>

            <QueueSection
              icon={<Mail className="h-4 w-4" />}
              title="Re-engagement drafts"
              count={nudges.length}
              empty="No re-engagement drafts waiting."
            >
              {nudges.map((n) => (
                <QueueRow
                  key={n.id}
                  avatar={n.student_name}
                  primary={n.student_name}
                  secondary={`${n.days_since_last_session} days since last lesson · ${n.subject}`}
                  to="/app/reengagement"
                  cta="Review & send"
                />
              ))}
            </QueueSection>

            <QueueSection
              icon={<Receipt className="h-4 w-4" />}
              title="Unbilled lessons"
              count={billingGroups.length}
              empty="No unbilled lessons."
            >
              {billingGroups.map((g) => {
                const firstSessionId = g.sessions[0]?.id;
                return (
                  <QueueRow
                    key={g.clientId}
                    avatar={g.clientName}
                    primary={g.clientName}
                    secondary={
                      `${g.sessions.length} session${g.sessions.length === 1 ? "" : "s"}` +
                      (g.estimatedTotalCents > 0
                        ? ` · ~${fmtMoney(g.estimatedTotalCents)}`
                        : "")
                    }
                    to={firstSessionId ? "/app/sessions/$sessionId" : "/app/invoices"}
                    params={firstSessionId ? { sessionId: firstSessionId } : undefined}
                    cta={g.sessions.length === 1 ? "Bill lesson" : "Review & bill"}
                  />
                );
              })}
            </QueueSection>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this recap?</AlertDialogTitle>
            <AlertDialogDescription>
              The lesson{confirmDraft ? ` with ${confirmDraft.name}` : ""} stays in your records
              and on the client's history — you just won't send a recap email. You can undo this
              for a few seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmId && handleDiscard(confirmId)}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function QueueSection({
  icon,
  title,
  count,
  empty,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-foreground/5 text-foreground/70">
          {icon}
        </span>
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        <span className="rounded-full bg-foreground/5 px-2 py-0.5 text-[11px] font-semibold text-foreground/70">
          {count}
        </span>
      </div>
      {count === 0 ? (
        <p className="px-1 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <Card className="divide-y divide-border overflow-hidden rounded-2xl p-0">
          {children}
        </Card>
      )}
    </section>
  );
}

function RecapRow({
  id,
  name,
  date,
  onDiscard,
}: {
  id: string;
  name: string;
  date: string;
  onDiscard: () => void;
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <Avatar className="h-9 w-9">
        <AvatarFallback className="bg-primary/20 text-xs font-semibold text-foreground">
          {initials(name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{name}</p>
        <p className="truncate text-xs text-muted-foreground">
          Lesson on {fmtDate(date)} · draft
        </p>
      </div>
      <Link
        to="/app/sessions/$sessionId"
        params={{ sessionId: id }}
        className="inline-flex items-center gap-1 rounded-md bg-foreground/5 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-foreground/10"
      >
        <Zap className="h-3 w-3" /> Review & send <ArrowRight className="h-3 w-3" />
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Recap actions"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onDiscard} className="text-destructive focus:text-destructive">
            <Trash2 className="mr-2 h-4 w-4" /> Discard recap
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function QueueRow({
  avatar,
  primary,
  secondary,
  to,
  params,
  cta,
}: {
  avatar: string;
  primary: string;
  secondary: string;
  to: string;
  params?: Record<string, string>;
  cta: string;
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <Avatar className="h-9 w-9">
        <AvatarFallback className="bg-primary/20 text-xs font-semibold text-foreground">
          {initials(avatar)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{primary}</p>
        <p className="truncate text-xs text-muted-foreground">{secondary}</p>
      </div>
      <Link
        to={to as any}
        params={params as any}
        className="inline-flex items-center gap-1 rounded-md bg-foreground/5 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-foreground/10"
      >
        <Zap className="h-3 w-3" /> {cta} <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
